import { ColumnSettings } from '@app/datatables/interfaces/column-settings';
import { DatatableRendering } from '@app/datatables/interfaces/datatable-rendering';
import { CustomColumnSettings } from '@app/datatables/models/custom-column-settings';
import { DatatableComponentDefinition } from '@app/datatables/models/datatable-component-definition.interface';
import { extendClassName } from '@app/datatables/utils/extend-class-name';
import { ColumnExport, SingleColumnTransformFunction } from '@app/shared/export';
import { Identifier } from '@app/utils/common';
import { isNotBlank } from '@app/utils/string';
import { StringKeys } from '@app/utils/string-keys';
import { DataTable } from '../../../globals';

export class Column<T, Model> implements CustomColumnSettings {
  /**
   * If a custom component has been defined then it will be created and attached to the column.
   */
  customComponent = undefined;

  /**
   * Set the default content to '' because otherwise datatables.net will throw an Error when a null
   * value is encountered.
   */
  defaultContent: string = '';

  export: ColumnExport<Model> | false = false;
  filter?: (value: Model, search: string) => boolean;
  hiddenInSelectMode: boolean = false;
  isOptional: boolean = false;
  visible: boolean = true;

  constructor(
    public data: string | undefined,
    public options?: ColumnSettings<T, Model>
  ) {
    if (this.options === undefined) {
      this.options = {};
    }

    if (this.options.name === undefined && isNotBlank(data)) {
      this.options.name = data;
    }

    this.render = DataTable.render.text();
  }

  hideInSelectMode: () => this = () => {
    this.hiddenInSelectMode = true;

    return this;
  };

  /**
   * Hide this column on mobile devices
   */
  hideOnMobile: () => this = extendClassName(this.options, 'not-mobile');

  /**
   * Hide this column on tablet devices
   */
  hideOnTablet: () => this = extendClassName(this.options, 'not-tablet');

  /**
   * Hide this column on desktop devices
   */
  hideOnDesktop: () => this = extendClassName(this.options, 'not-desktop');

  /**
   * Make this column not sortable
   */
  notSortable: () => this = () => {
    this.options.orderable = false;

    return this;
  };

  /**
   * Dummy callback function that is overwritten when calling withOnAfterCreate.
   *
   * @param row The object that contains the data for the row of this column
   * @param el
   */
  onAfterCreate(row: Model, el): void {}

  /**
   * Mark the column as optional, the visibility can be toggled.
   * @param visible If the column should be visible by default.
   */
  optional: (visible?: boolean) => this = (visible: boolean = false) => {
    this.isOptional = true;
    this.visible = visible;

    return this;
  };

  /**
   * Default render function that is called by the datatable implementation when creating the column.
   */
  render: DatatableRendering<T, Model> = (data: any) => {
    return data;
  };

  /**
   * Show this column on all devices
   */
  showAlways: () => this = extendClassName(this.options, 'all');

  /**
   * Show this column on mobile devices
   */
  showOnMobile: () => this = extendClassName(this.options, 'mobile');

  /**
   * Show this column on tablet devices
   */
  showOnTablet: () => this = extendClassName(this.options, 'tablet');

  /**
   * Show this column on desktop devices
   */
  showOnDesktop: () => this = extendClassName(this.options, 'desktop');

  /**
   * Returns a comparable value for this column.
   */
  sort?: (a: Model) => any;

  withCustomComponent(c: DatatableComponentDefinition): Column<T, Model> {
    this.customComponent = c;
    this.render = c.render || this.render;

    return this;
  }

  /**
   * Define the header that is used when exporting the table.
   *
   * @param header i18n key that is used as the header of this column.
   */
  withExport(header: string): Column<T, Model>;

  /**
   * Define the header as well as the transform function to be used when exporting the table.
   *
   * @param header i18n key or a function that returns a string that is used as the header of this column.
   * @param transform A function that gets the data object of each row. The returned value is exported.
   */
  withExport(
    header: string,
    transform: SingleColumnTransformFunction<Model> | StringKeys<Model>
  ): Column<T, Model>;

  /**
   * Define the column export options. Can be for either a single-column or a multi-column export.
   *
   * @param options Column export options. For more details see the documentation of SingleColumnExport
   *                and MultiColumnExport.
   */
  withExport(options: ColumnExport<Model>): Column<T, Model>;

  withExport(
    headerOrExportFunction: string | ColumnExport<Model>,
    transform?: SingleColumnTransformFunction<Model>
  ): Column<T, Model> {
    this.export = {
      header: this.options.name,
      transform: (entry) => entry[this.options.name],
    };

    if (transform === undefined) {
      if (typeof headerOrExportFunction === 'string') {
        this.export.header = headerOrExportFunction;
      } else if (typeof headerOrExportFunction === 'object') {
        this.export = headerOrExportFunction;
      } else {
        this.export.transform = headerOrExportFunction;
      }
    } else {
      this.export.header = headerOrExportFunction as string;
      this.export.transform = transform;
    }

    return this;
  }

  /**
   * Function that indicates if the given row matches the search term.
   *
   * @param filter Function is called whenever the filter is updated. If it
   * returns true then the row is rendered.
   */
  withFilter(filter: (value: Model, search: string) => boolean): Column<T, Model> {
    this.filter = filter;

    return this;
  }

  /**
   * Define a function that is called after the column has been created.
   */
  withOnAfterCreate(f: (row: Model, el) => void): Column<T, Model> {
    this.onAfterCreate = f;

    return this;
  }

  /**
   * Define a function that conforms to the datatables.net definition of a render function.
   * See https://datatables.net/reference/option/columns.render for more information.
   */
  withRender(f: DatatableRendering<T, Model>): Column<T, Model> {
    this.render = f;

    return this;
  }

  /**
   * Define a function that takes the original object and returns a comparable value for this column.
   *
   * @param f Function that returns a value that can be used to compare values in this column.
   */
  withSort(f: (a: Model) => Identifier) {
    this.sort = f;

    return this;
  }
}
